import { useState } from "react";
import { useLanguage, API } from "../App";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Download, BookOpen, Check, ExternalLink } from "lucide-react";
import { trackFormSubmit } from "../utils/analytics";
import { useRecaptchaToken } from "../hooks/useRecaptchaToken";

const EBOOK_URL = "/ebook";

const labels = {
  fr: {
    title: "Téléchargez votre guide maintenant",
    subtitle: "Le guide complet de la gestion documentaire pour les entreprises",
    email: "Email",
    company: "Entreprise (optionnel)",
    download: "Télécharger le guide",
    orOpen: "Ou ouvrir sans inscription",
    contents: [
      "Introduction à la GED",
      "Checklist RGPD + facturation électronique",
      "Comparatif fonctionnalités GED",
      "Cas pratiques",
      "Recommandations + Analyste IA",
      "ROI et indicateurs de succès"
    ]
  },
  es: {
    title: "Descarga tu guía ahora",
    subtitle: "La guía completa de gestión documental para empresas",
    email: "Email",
    company: "Empresa (opcional)",
    download: "Descargar guía",
    orOpen: "O abrir sin registro",
    contents: [
      "Introducción a la GED",
      "Checklist RGPD + facturación electrónica",
      "Comparativa funcionalidades GED",
      "Casos prácticos",
      "Recomendaciones + Analista IA",
      "ROI e indicadores de éxito"
    ]
  },
  en: {
    title: "Download your guide now",
    subtitle: "The complete document management guide for businesses",
    email: "Email",
    company: "Company (optional)",
    download: "Download guide",
    orOpen: "Or open without registration",
    contents: [
      "Introduction to DMS",
      "GDPR + e-invoicing checklist",
      "DMS features comparison",
      "Practical case studies",
      "Recommendations + AI Analyst",
      "ROI and success indicators"
    ]
  }
};

export const LeadMagnet = () => {
  const { language } = useLanguage();
  const lm = labels[language] || labels.fr;
  const [formData, setFormData] = useState({ email: "", company: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getRecaptchaToken = useRecaptchaToken();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) return;
    setIsSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken("lead_magnet");
      await axios.post(`${API}/lead-magnet`, {
        name: formData.email.split("@")[0],
        email: formData.email,
        company: formData.company,
        language,
        recaptchaToken
      });
      trackFormSubmit("ebook_download", language);
      toast.success(language === "fr" ? "Merci ! Redirection..." : language === "es" ? "Gracias! Redirigiendo..." : "Thanks! Redirecting...");
      setFormData({ email: "", company: "" });
      setTimeout(() => window.location.href = EBOOK_URL, 500);
    } catch (error) {
      console.error("Lead magnet error:", error?.response?.data || error);
      toast.error(language === "fr" ? "Erreur — réessayez" : language === "es" ? "Error — inténtelo de nuevo" : "Error — please retry");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDirect = () => {
    trackFormSubmit("ebook_direct_open", language);
    window.location.href = EBOOK_URL;
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="lead-magnet-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="rounded-2xl overflow-hidden shadow-xl"
      >
        <div className="grid md:grid-cols-2">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 md:p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <BookOpen size={28} className="text-blue-400" />
              </div>
              <h3 className="font-playfair text-2xl md:text-3xl font-bold">{lm.title}</h3>
            </div>
            <p className="text-slate-300 mb-8 text-base">{lm.subtitle}</p>
            <div className="space-y-3">
              {lm.contents.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <Check size={14} className="text-blue-400" />
                  </div>
                  <span className="text-slate-200 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="bg-white p-8 md:p-12 flex flex-col justify-center">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{lm.email} *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  required
                  placeholder="you@company.com"
                  data-testid="lead-magnet-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{lm.company}</label>
                <Input
                  value={formData.company}
                  onChange={e => setFormData(p => ({ ...p, company: e.target.value }))}
                  placeholder="Company Inc."
                  data-testid="lead-magnet-company"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-base font-semibold rounded-xl"
                data-testid="lead-magnet-submit"
              >
                <Download size={18} className="mr-2" />
                {lm.download}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={handleOpenDirect}
                className="text-sm text-slate-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-1.5 mx-auto"
                data-testid="lead-magnet-direct"
              >
                <ExternalLink size={14} />
                {lm.orOpen}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LeadMagnet;
